/**
 * TTS settings — voice selection, sentence count, on/off.
 *
 * Persisted to localStorage so the user's voice choice survives reloads.
 * Both the desktop and web builds share this store; the web build simply
 * never surfaces the toggle UI.
 */
import { create } from "zustand";
import { mirroredStore } from "@/lib/mirrored-store";
import {
  setVoiceEnginePreference,
  setWindowsVoicePreference,
} from "@/desktop/voice-engine-preference";
import {
  isDenkerAgentVoiceId,
  type DenkerAgentVoiceId,
} from "@/desktop/denker-agent-voices";

const STORAGE_KEY = "denker.tts-settings";
export const DEFAULT_DENKER_AGENT_VOICE_ID: DenkerAgentVoiceId = "michael";

export type TtsSettings = {
  enabled: boolean;
  /** Legacy Apple voice URI kept only so old persisted settings can hydrate. */
  voiceURI: string | null;
  /** Legacy provider field; read-aloud now always uses Denker ONNX voices. */
  voiceProvider: "apple" | "denker";
  denkerVoiceId: DenkerAgentVoiceId | null;
  voiceSetupSkipped: boolean;
  /**
   * Which TTS engine to speak with, when the user has a choice.
   *
   * `null` means the Denker voices. Only Windows offers a real choice: the
   * Denker voices and the Windows OneCore engine both perform well there, so
   * it is a taste question — branded and expressive, or instant and robotic.
   * Every Mac speaks with the Denker voices.
   */
  voiceEngine: "kokoro" | "system" | null;
  /**
   * The Windows OneCore voice to speak with, as its WinRT voice id.
   *
   * Kept apart from `denkerVoiceId` because the two are different kinds of
   * thing: Denker has exactly four curated voices that ship with the app, while
   * the Windows set is whatever the user has installed and can grow at any time
   * from Settings. `null` falls back to the Windows default voice.
   */
  windowsVoiceId: string | null;
  sentenceCount: number;
  /** True iff read-aloud is usable. Denker ONNX voices set this true locally. */
  premiumAvailable: boolean;
  /**
   * Answer in text only, with a soft cue, while another app plays sound
   * (music, a video) or uses the microphone (a call) - `desktop/tts-quiet-turns.ts`.
   * Off by default.
   */
  quietWhenHeard: boolean;
};

type Actions = {
  setEnabled: (enabled: boolean) => void;
  toggleEnabled: () => void;
  setVoiceURI: (uri: string | null) => void;
  setDenkerVoice: (voiceId: TtsSettings["denkerVoiceId"]) => void;
  setVoiceEngine: (engine: TtsSettings["voiceEngine"]) => void;
  setWindowsVoice: (voiceId: string | null) => void;
  skipDenkerVoiceSetup: () => void;
  setSentenceCount: (count: number) => void;
  setPremiumAvailable: (available: boolean) => void;
  setQuietWhenHeard: (quiet: boolean) => void;
};

type StoredTtsSettings = Partial<Omit<TtsSettings, "denkerVoiceId">> & {
  denkerVoiceId?: unknown;
};

const DEFAULTS: TtsSettings = {
  enabled: false,
  voiceURI: null,
  voiceProvider: "denker",
  denkerVoiceId: DEFAULT_DENKER_AGENT_VOICE_ID,
  voiceSetupSkipped: false,
  voiceEngine: null,
  windowsVoiceId: null,
  sentenceCount: 2,
  premiumAvailable: true,
  quietWhenHeard: false,
};

/**
 * Retired ids keep their slot: the Kokoro-era ids saved before the Pocket
 * voices, and Vera and Daan, which Eve and Michael replaced.
 */
const RETIRED_VOICE_IDS = new Map<unknown, DenkerAgentVoiceId>([
  ["af_heart", "eponine"],
  ["af_bella", "eve"],
  ["am_puck", "george"],
  ["am_fenrir", "michael"],
  ["af_jessica", "eponine"],
  ["af_sky", "eve"],
  ["am_adam", DEFAULT_DENKER_AGENT_VOICE_ID],
  ["vera", "eve"],
  ["daan", "michael"],
]);

function parseDenkerVoiceId(value: unknown): TtsSettings["denkerVoiceId"] {
  if (isDenkerAgentVoiceId(value)) return value;
  return RETIRED_VOICE_IDS.get(value) ?? null;
}

function parseVoiceEngine(value: unknown): TtsSettings["voiceEngine"] {
  return value === "kokoro" || value === "system" ? value : null;
}

function loadInitial(): TtsSettings {
  if (typeof window === "undefined") return DEFAULTS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as StoredTtsSettings;
    const voiceSetupSkipped =
      typeof parsed.voiceSetupSkipped === "boolean"
        ? parsed.voiceSetupSkipped
        : DEFAULTS.voiceSetupSkipped;
    const denkerVoiceId = voiceSetupSkipped
      ? null
      : (parseDenkerVoiceId(parsed.denkerVoiceId) ??
        (parsed.voiceURI
          ? DEFAULT_DENKER_AGENT_VOICE_ID
          : DEFAULTS.denkerVoiceId));
    return {
      enabled:
        typeof parsed.enabled === "boolean" ? parsed.enabled : DEFAULTS.enabled,
      voiceURI: null,
      voiceProvider: "denker",
      denkerVoiceId,
      voiceSetupSkipped,
      voiceEngine: parseVoiceEngine(parsed.voiceEngine),
      windowsVoiceId:
        typeof parsed.windowsVoiceId === "string" && parsed.windowsVoiceId
          ? parsed.windowsVoiceId
          : null,
      sentenceCount:
        typeof parsed.sentenceCount === "number" && parsed.sentenceCount > 0
          ? Math.min(10, Math.floor(parsed.sentenceCount))
          : DEFAULTS.sentenceCount,
      premiumAvailable: denkerVoiceId ? true : DEFAULTS.premiumAvailable,
      quietWhenHeard:
        typeof parsed.quietWhenHeard === "boolean"
          ? parsed.quietWhenHeard
          : DEFAULTS.quietWhenHeard,
    };
  } catch {
    return DEFAULTS;
  }
}

/** Mirror the engine choice into the module the speak path reads synchronously.
 *  Wired to the store's subscription below rather than to the actions, so that
 *  state arriving from another window lands here too. */
function publishVoicePreferences(state: TtsSettings) {
  setVoiceEnginePreference(state.voiceEngine);
  setWindowsVoicePreference(state.windowsVoiceId);
}

function persist(state: TtsSettings) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        enabled: state.enabled,
        voiceURI: state.voiceURI,
        voiceProvider: state.voiceProvider,
        denkerVoiceId: state.denkerVoiceId,
        voiceSetupSkipped: state.voiceSetupSkipped,
        voiceEngine: state.voiceEngine,
        windowsVoiceId: state.windowsVoiceId,
        sentenceCount: state.sentenceCount,
        premiumAvailable: state.premiumAvailable,
        quietWhenHeard: state.quietWhenHeard,
      }),
    );
  } catch {
    // Quota or private-mode failures are non-fatal — ignore.
  }
}

const INITIAL_TTS_SETTINGS = loadInitial();
publishVoicePreferences(INITIAL_TTS_SETTINGS);

export const useTtsSettingsStore = create<TtsSettings & Actions>()(
  mirroredStore("ttsSettings", (set, get) => ({
    ...INITIAL_TTS_SETTINGS,
    setEnabled: (enabled) => {
      set({ enabled });
      persist(get());
    },
    toggleEnabled: () => {
      set({ enabled: !get().enabled });
      persist(get());
    },
    setVoiceURI: (_voiceURI) => {
      set({ voiceURI: null, voiceProvider: "denker" });
      persist(get());
    },
    setDenkerVoice: (denkerVoiceId) => {
      set({
        denkerVoiceId,
        voiceProvider: denkerVoiceId ? "denker" : get().voiceProvider,
        premiumAvailable: denkerVoiceId ? true : get().premiumAvailable,
        enabled: denkerVoiceId ? true : get().enabled,
        voiceSetupSkipped: false,
      });
      persist(get());
    },
    setVoiceEngine: (voiceEngine) => {
      set({ voiceEngine });
      persist(get());
    },
    setWindowsVoice: (windowsVoiceId) => {
      // Choosing a voice is also the act of finishing voice setup, mirroring
      // `setDenkerVoice` — otherwise picking a Windows voice would leave
      // read-aloud switched off.
      set({
        windowsVoiceId,
        enabled: windowsVoiceId ? true : get().enabled,
        premiumAvailable: windowsVoiceId ? true : get().premiumAvailable,
        voiceSetupSkipped: windowsVoiceId ? false : get().voiceSetupSkipped,
      });
      persist(get());
    },
    skipDenkerVoiceSetup: () => {
      set({
        enabled: false,
        voiceURI: null,
        voiceProvider: "denker",
        denkerVoiceId: null,
        windowsVoiceId: null,
        premiumAvailable: false,
        voiceSetupSkipped: true,
      });
      persist(get());
    },
    setSentenceCount: (sentenceCount) => {
      const clamped = Math.max(1, Math.min(10, Math.floor(sentenceCount)));
      set({ sentenceCount: clamped });
      persist(get());
    },
    setPremiumAvailable: (premiumAvailable) => {
      set({ premiumAvailable });
      persist(get());
    },
    setQuietWhenHeard: (quietWhenHeard) => {
      set({ quietWhenHeard });
      persist(get());
    },
  })),
);

// Republish on EVERY state change rather than from inside the actions.
// Denker runs several windows against one mirrored store, and a change made in
// a settings window reaches the others — including the owner window, the only
// one that actually speaks — as a raw `setState` that bypasses the actions
// entirely. Publishing from the write path alone would leave those windows
// showing one engine in the UI while still speaking through the other.
useTtsSettingsStore.subscribe(publishVoicePreferences);

/**
 * Forget the TTS settings — used by account deletion. The app keeps running
 * afterwards, so the live state goes too: left in place, the next change would
 * write the deleted account's choices straight back.
 */
export function clearPersistedTtsSettings(): void {
  useTtsSettingsStore.setState(DEFAULTS);
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable — nothing to clear */
  }
}
